import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useInvoiceById, useSendDraftInvoice } from "@/api/invoices";
import { formatCurrency } from "@/lib/format";
import { getCustomerName } from "@/lib/safe-access";
import { isUnsentInvoice } from "./invoice-send-eligibility";
import { InvoiceDueDateDisplay } from "./invoice-due-date-display";

interface ReviewInvoiceDialogProps {
  invoiceId: string | null;
  onClose: () => void;
}

export function ReviewInvoiceDialog({
  invoiceId,
  onClose,
}: ReviewInvoiceDialogProps) {
  const {
    data: invoice,
    isLoading,
    isError,
    refetch,
  } = useInvoiceById(invoiceId);
  const sendInvoice = useSendDraftInvoice();
  const canSend =
    !!invoice &&
    isUnsentInvoice(invoice) &&
    !!invoice.dueDateOnSend &&
    invoice.invoiceOrders.length > 0;

  const handleSend = () => {
    if (!invoice || !canSend) return;
    sendInvoice.mutate(
      { invoiceId: invoice.id },
      {
        onSuccess: () => {
          toast.success("Invoice sent to Stripe and customer");
          onClose();
        },
        onError: (error) => {
          const message = axios.isAxiosError<{ error?: string }>(error)
            ? error.response?.data?.error
            : undefined;
          toast.error("Failed to send invoice", {
            description: message ?? error.message,
          });
        },
      }
    );
  };

  return (
    <Dialog open={!!invoiceId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Review invoice before sending</DialogTitle>
          <DialogDescription>
            Check the included orders, their shipments, and the due date.
            Sending creates the Stripe invoice and emails the customer.
          </DialogDescription>
        </DialogHeader>

        {isLoading && <p>Loading invoice...</p>}
        {isError && (
          <div>
            <p>Could not load the invoice.</p>
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        )}
        {invoice && (
          <div className="space-y-4">
            <div className="text-sm">
              <p className="font-medium">{getCustomerName(invoice.customer)}</p>
              <p>
                {invoice.invoiceOrders.length} orders ·{" "}
                {invoice.totalCents != null
                  ? formatCurrency(invoice.totalCents)
                  : "Amount unavailable"}
              </p>
              <InvoiceDueDateDisplay invoice={invoice} fallback="Unavailable" />
            </div>

            <div className="max-h-64 overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Included order</TableHead>
                    <TableHead>Shipment numbers</TableHead>
                    <TableHead className="text-right">
                      Balance to invoice
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.invoiceOrders.map(({ order }) => (
                    <TableRow key={order.id}>
                      <TableCell>#{order.orderNumber}</TableCell>
                      <TableCell>
                        {order.shipments.length > 0
                          ? order.shipments
                              .map((shipment) => `#${shipment.shipmentNumber}`)
                              .join(", ")
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {order.balanceCents != null
                          ? formatCurrency(order.balanceCents)
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {!canSend && (
              <p className="text-sm text-muted-foreground">
                This invoice cannot be sent in its current state.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!canSend || sendInvoice.isPending}
            onClick={handleSend}
          >
            {sendInvoice.isPending ? "Sending..." : "Finalize & Send Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
